"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 16: Bandai FCG / LZ93D50
 * ------------------------------------------------------------------ *
 * Le numero iNES 16 recouvre deux ASIC de Bandai:
 *
 *   - FCG-1/FCG-2 (Dragon Ball, Famicom Jump...): pas d'EEPROM, registres
 *     dans $6000-$7FFF, compteur d'IRQ ecrit directement.
 *   - LZ93D50 (Dragon Ball Z II/III/Gaiden, SD Gundam...): EEPROM serie
 *     24C02, registres dans $8000-$FFFF, compteur d'IRQ a verrou (latch).
 *
 * Les deux ont le meme plan de banques:
 *
 *   - $8000-$BFFF: un banc PRG de 16 Ko, choisi par les 4 bits bas du
 *     registre $8.
 *   - $C000-$FFFF: le dernier banc PRG, fixe (vecteurs de reset/NMI/IRQ).
 *   - PPU $0000-$1FFF: huit fenetres CHR de 1 Ko independantes, registres
 *     $0-$7.
 *
 * L'en-tete iNES 1.0 ne porte pas le submapper NES 2.0. On decode donc les
 * deux plages d'adresses, comme le demande le submapper 0 de NESdev
 * ("Emulate both FCG-1/2 and LZ93D50 chips in their respective CPU address
 * ranges"). Le mode IRQ suit la derniere fenetre utilisee: ecrire $6000-$7FFF
 * selectionne le compteur direct du FCG, ecrire $8000-$FFFF le verrou du
 * LZ93D50. Aucune carte reelle ne melange les deux, donc ce choix ne depend
 * pas d'une option utilisateur.
 *
 * Reference: https://www.nesdev.org/wiki/INES_Mapper_016
 * ------------------------------------------------------------------ */
// Bandai FCG-1/FCG-2 and LZ93D50

// Fenetres CHR de 1 Ko, dans l'ordre des registres $0-$7.
var CHR_WINDOWS = [
  0x0000, 0x0400, 0x0800, 0x0c00, 0x1000, 0x1400, 0x1800, 0x1c00,
];

/* ------------------------------------------------------------------ *
 * EEPROM serie 24C02 (256 octets, pages de 8 octets)
 * ------------------------------------------------------------------ *
 * Le LZ93D50 ne donne pas un port parallele a l'EEPROM: il expose deux
 * lignes I2C sur le registre $800D, SCL en bit 5 et SDA en bit 6. La sorte
 * de l'EEPROM revient sur le bit 4 des lectures $6000-$7FFF.
 *
 * On modelise le protocole au niveau des fronts, pas au niveau des octets:
 *   - START: SDA passe de 1 a 0 pendant que SCL est haut.
 *   - STOP : SDA passe de 0 a 1 pendant que SCL est haut.
 *   - Les bits sont echantillonnes sur le front montant de SCL.
 *   - L'EEPROM acquitte chaque octet en tirant SDA a 0 au neuvieme coup.
 *
 * Les jeux qui sauvegardent (Dragon Ball Z) ecrivent l'adresse puis les
 * donnees, et relisent avec un START repete. Une page de 8 octets reboucle
 * dans la page, comme sur la puce.
 * ------------------------------------------------------------------ */
function Eeprom24C02() {
  this.data = new Uint8Array(256);
  this.onWrite = null;
  this.resetBus();
}

Eeprom24C02.prototype.resetBus = function () {
  this.mode = "idle";
  this.nextMode = "idle";
  this.bitCount = 0;
  this.shift = 0;
  this.wordAddress = 0;
  this.readByte = 0;
  this.output = 1;
  this.prevScl = 1;
  this.prevSda = 1;
  this.deviceRead = false;
};

Eeprom24C02.prototype.read = function () {
  return this.output;
};

Eeprom24C02.prototype.start = function () {
  this.mode = "device";
  this.nextMode = "idle";
  this.bitCount = 0;
  this.shift = 0;
  this.output = 1;
};

Eeprom24C02.prototype.stop = function () {
  this.mode = "idle";
  this.output = 1;
};

// Un front montant de SCL: on echantillonne SDA. En lecture, c'est aussi le
// moment ou le maitre presente son ACK/NACK apres les huit bits de donnee.
Eeprom24C02.prototype.clockRise = function (sda) {
  switch (this.mode) {
    case "device":
    case "address":
    case "write":
      if (this.bitCount < 8) {
        this.shift = ((this.shift << 1) | sda) & 0xff;
        this.bitCount++;
      }
      break;

    case "read":
      if (this.bitCount < 8) {
        this.bitCount++;
      }
      break;

    case "readAck":
      // ACK = 0: le maitre veut l'octet suivant. NACK = 1: fin de lecture.
      if (sda === 0) {
        this.wordAddress = (this.wordAddress + 1) & 0xff;
        this.readByte = this.data[this.wordAddress];
        this.mode = "read";
        this.bitCount = 0;
        this.output = (this.readByte >> 7) & 1;
      } else {
        this.mode = "idle";
        this.output = 1;
      }
      break;
  }
};

// Un front descendant de SCL: l'octet courant est complet, on passe a
// l'acquittement ou a l'octet suivant. Pendant le neuvieme coup d'horloge,
// output reste a 0: c'est l'ACK de l'EEPROM.
Eeprom24C02.prototype.clockFall = function () {
  switch (this.mode) {
    case "device":
      if (this.bitCount === 8) {
        // Adresse de composant 1010 A2 A1 A0, plus le bit R/W.
        if ((this.shift & 0xf0) === 0xa0) {
          this.deviceRead = (this.shift & 1) === 1;
          this.mode = "ack";
          this.nextMode = this.deviceRead ? "read" : "address";
          this.output = 0;
          if (this.deviceRead) {
            this.readByte = this.data[this.wordAddress];
          }
        } else {
          // Ce n'est pas notre EEPROM: on relache le bus.
          this.mode = "idle";
          this.output = 1;
        }
        this.bitCount = 0;
      }
      break;

    case "address":
      if (this.bitCount === 8) {
        this.wordAddress = this.shift;
        this.mode = "ack";
        this.nextMode = "write";
        this.output = 0;
        this.bitCount = 0;
      }
      break;

    case "write":
      if (this.bitCount === 8) {
        this.data[this.wordAddress] = this.shift;
        if (this.onWrite !== null) {
          this.onWrite(this.wordAddress, this.shift);
        }
        // 24C02: pages de 8 octets. L'adresse reboucle dans la page.
        this.wordAddress =
          (this.wordAddress & 0xf8) | ((this.wordAddress + 1) & 0x07);
        this.mode = "ack";
        this.nextMode = "write";
        this.output = 0;
        this.bitCount = 0;
      }
      break;

    case "ack":
      this.mode = this.nextMode;
      this.bitCount = 0;
      this.shift = 0;
      this.output = 1;
      if (this.mode === "read") {
        this.output = (this.readByte >> 7) & 1;
      }
      break;

    case "read":
      if (this.bitCount === 8) {
        this.mode = "readAck";
        this.output = 1;
        this.bitCount = 0;
      } else {
        // Les donnees changent pendant que SCL est bas, comme sur l'I2C.
        this.output = (this.readByte >> (7 - this.bitCount)) & 1;
      }
      break;
  }
};

Eeprom24C02.prototype.write = function (scl, sda) {
  scl = scl ? 1 : 0;
  sda = sda ? 1 : 0;

  var prevScl = this.prevScl;
  var prevSda = this.prevSda;

  if (prevScl === 1 && scl === 1 && prevSda === 1 && sda === 0) {
    this.start();
  } else if (prevScl === 1 && scl === 1 && prevSda === 0 && sda === 1) {
    this.stop();
  } else if (prevScl === 0 && scl === 1) {
    this.clockRise(sda);
  } else if (prevScl === 1 && scl === 0) {
    this.clockFall();
  }

  this.prevScl = scl;
  this.prevSda = sda;
};

Eeprom24C02.prototype.toJSON = function () {
  return {
    mode: this.mode,
    nextMode: this.nextMode,
    bitCount: this.bitCount,
    shift: this.shift,
    wordAddress: this.wordAddress,
    readByte: this.readByte,
    output: this.output,
    prevScl: this.prevScl,
    prevSda: this.prevSda,
    deviceRead: this.deviceRead,
  };
};

Eeprom24C02.prototype.fromJSON = function (s) {
  if (!s) {
    this.resetBus();
    return;
  }
  this.mode = s.mode || "idle";
  this.nextMode = s.nextMode || "idle";
  this.bitCount = s.bitCount | 0;
  this.shift = s.shift | 0;
  this.wordAddress = s.wordAddress | 0;
  this.readByte = s.readByte | 0;
  this.output = s.output === undefined ? 1 : s.output & 1;
  this.prevScl = s.prevScl === undefined ? 1 : s.prevScl & 1;
  this.prevSda = s.prevSda === undefined ? 1 : s.prevSda & 1;
  this.deviceRead = s.deviceRead === true;
};

function register(Mappers) {
  Mappers[16] = function (nes) {
    this.nes = nes;

    var self = this;
    this.eeprom = new Eeprom24C02();
    this.eeprom.onWrite = function (address, value) {
      self.saveEepromByte(address, value);
    };
  };

  Mappers[16].prototype = new Mappers[0]();

  // Le compteur d'IRQ compte des cycles CPU (M2), pas des lignes de balayage.
  Mappers[16].prototype.watchesCpuCycles = true;

  Mappers[16].prototype.reset = function () {
    Mappers[0].prototype.reset.apply(this);

    // Huit fenetres CHR de 1 Ko, un banc PRG de 16 Ko a $8000.
    this.chrBanks = [0, 0, 0, 0, 0, 0, 0, 0];
    this.prgBank = 0;

    // Le registre de miroir suit l'en-tete tant que le jeu n'ecrit pas $9.
    this.mirroring = this.nes.rom ? this.nes.rom.mirroring : 0;
    this.applyMirroring();

    // IRQ: compteur et verrou separes. Par defaut on est en mode LZ93D50
    // (verrou); la premiere ecriture dans $6000-$7FFF basculera en FCG.
    this.irqEnabled = false;
    this.irqCounter = 0;
    this.irqLatch = 0;
    this.irqAsserted = false;
    this.irqLatchMode = true;

    // L'EEPROM garde ses donnees a travers un reset: seules les lignes I2C
    // reviennent au repos.
    this.eeprom.resetBus();
  };

  Mappers[16].prototype.load = function (address) {
    address &= 0xffff;

    // $6000-$7FFF: pas de WRAM sur ces cartes. Le seul emetteur est la
    // sortie de l'EEPROM, sur le bit 4; le reste est du bus ouvert.
    if (address >= 0x6000 && address < 0x8000) {
      var openBus = this.nes.cpu.mem[address] || 0;
      return ((openBus & 0xef) | (this.eeprom.read() << 4)) & 0xff;
    }

    return Mappers[0].prototype.load.apply(this, arguments);
  };

  Mappers[16].prototype.write = function (address, value) {
    if (address >= 0x6000 && address < 0x8000) {
      // Fenetre FCG-1/2: le registre est le nibble bas de l'adresse.
      this.writeRegister(address & 0x0f, value, false);
      return;
    }

    if (address >= 0x8000) {
      // Fenetre LZ93D50: meme decodage, mais sur tout $8000-$FFFF.
      this.writeRegister(address & 0x0f, value, true);
      return;
    }

    Mappers[0].prototype.write.apply(this, arguments);
  };

  // `lzWindow` dit quelle plage a recu l'ecriture; c'est elle qui choisit le
  // mode du compteur d'IRQ (verrou pour le LZ93D50, direct pour le FCG).
  Mappers[16].prototype.writeRegister = function (reg, value, lzWindow) {
    if (reg <= 0x0d) {
      this.irqLatchMode = lzWindow;
    }

    if (reg <= 0x07) {
      // $0-$7: huit bancs CHR de 1 Ko. La valeur est complete (8 bits).
      this.chrBanks[reg] = value & 0xff;
      this.load1kVromBank(this.chrBanks[reg], CHR_WINDOWS[reg]);
      return;
    }

    switch (reg) {
      case 0x08:
        // $8: banc PRG de 16 Ko a $8000. Seuls les 4 bits bas existent.
        this.prgBank = value & 0x0f;
        this.loadRomBank(this.prgBank, 0x8000);
        return;

      case 0x09:
        // $9: 0 = vertical, 1 = horizontal, 2/3 = une seule page.
        this.mirroring = value & 0x03;
        this.applyMirroring();
        return;

      case 0x0a:
        // $A: bit 0 autorise le comptage. Toute ecriture acquitte l'IRQ.
        // Sur le LZ93D50, elle recopie aussi le verrou dans le compteur.
        this.irqEnabled = (value & 0x01) !== 0;
        if (this.irqLatchMode) {
          this.irqCounter = this.irqLatch;
        }
        this.irqAsserted = false;
        return;

      case 0x0b:
        // $B: octet bas du compteur (FCG) ou du verrou (LZ93D50).
        if (this.irqLatchMode) {
          this.irqLatch = (this.irqLatch & 0xff00) | (value & 0xff);
        } else {
          this.irqCounter = (this.irqCounter & 0xff00) | (value & 0xff);
        }
        return;

      case 0x0c:
        // $C: octet haut.
        if (this.irqLatchMode) {
          this.irqLatch = (this.irqLatch & 0x00ff) | ((value & 0xff) << 8);
        } else {
          this.irqCounter = (this.irqCounter & 0x00ff) | ((value & 0xff) << 8);
        }
        return;

      case 0x0d:
        // $D: SCL en bit 5, SDA en bit 6 (le bit 7 est le sens de lecture).
        this.eeprom.write((value >> 5) & 1, (value >> 6) & 1);
        return;
    }

    // $E/$F ne sont pas decodes par ces cartes.
  };

  Mappers[16].prototype.applyMirroring = function () {
    switch (this.mirroring & 0x03) {
      case 0:
        this.setMirroring(this.nes.rom.VERTICAL_MIRRORING);
        break;
      case 1:
        this.setMirroring(this.nes.rom.HORIZONTAL_MIRRORING);
        break;
      case 2:
        this.setMirroring(this.nes.ppu.MIRRORING_TYPES.SINGLE_SCREEN);
        break;
      default:
        this.setMirroring(this.nes.ppu.MIRRORING_TYPES.SINGLE_SCREEN_ALT);
        break;
    }
  };

  Mappers[16].prototype.applyBanks = function () {
    for (var i = 0; i < 8; i++) {
      this.load1kVromBank(this.chrBanks[i], CHR_WINDOWS[i]);
    }
    this.loadRomBank(this.prgBank, 0x8000);
    this.loadRomBank(this.nes.rom.romCount - 1, 0xc000);
  };

  Mappers[16].prototype.saveEepromByte = function (address, value) {
    var ram = this.nes.rom.batteryRamData;
    if (ram !== null && ram !== undefined && address < ram.length) {
      ram[address] = value;
    }
    if (typeof this.nes.opts.onBatteryRamWrite === "function") {
      this.nes.opts.onBatteryRamWrite(0x6000 + address, value);
    }
  };

  Mappers[16].prototype.loadBatteryRam = function () {
    // L'EEPROM fait 256 octets, alors que batteryRamData est dimensionne pour
    // une WRAM de 8 Ko. On ne recopie que les 256 premiers octets et on ne
    // touche pas a $6000-$7FFF, qui est le port I2C et non de la RAM.
    var ram = this.nes.rom.batteryRamData;
    if (ram !== null && ram !== undefined) {
      var count = Math.min(256, ram.length);
      for (var i = 0; i < count; i++) {
        this.eeprom.data[i] = ram[i];
      }
    }
  };

  // Le compteur est decrmente a chaque cycle M2. L'IRQ part quand il vaut
  // deja zero au debut du cycle (c'est la formulation retenue par Mesen pour
  // que le meme compteur convienne aux familles 16, 153 et 159). La ligne
  // reste basse jusqu'a l'ecriture d'acquittement dans $A.
  Mappers[16].prototype.clockCpuCycles = function (cycles) {
    if (this.irqAsserted) {
      this.nes.cpu.requestIrq(this.nes.cpu.IRQ_NORMAL);
    }

    if (!this.irqEnabled) {
      return;
    }

    for (var i = 0; i < cycles; i++) {
      if (this.irqCounter === 0) {
        this.irqAsserted = true;
        this.nes.cpu.requestIrq(this.nes.cpu.IRQ_NORMAL);
      }
      this.irqCounter = (this.irqCounter - 1) & 0xffff;
    }
  };

  Mappers[16].prototype.loadROM = function () {
    if (!this.nes.rom.valid) {
      throw new Error("Bandai FCG: Invalid ROM! Unable to load.");
    }

    this.reset();
    this.loadCHRROM();
    this.loadBatteryRam();
    this.loadTrainer();

    // Power-on: les huit fenetres CHR sur le banc 0, le banc PRG 0 a $8000,
    // et le dernier banc fixe a $C000 (les vecteurs de reset y vivent).
    for (var i = 0; i < 8; i++) {
      this.load1kVromBank(0, CHR_WINDOWS[i]);
    }
    this.loadRomBank(0, 0x8000);
    this.loadRomBank(this.nes.rom.romCount - 1, 0xc000);
    this.applyMirroring();

    this.resetIrqHandler();
  };

  Mappers[16].prototype.toJSON = function () {
    var s = Mappers[0].prototype.toJSON.apply(this);
    s.chrBanks = this.chrBanks.slice();
    s.prgBank = this.prgBank;
    s.mirroring = this.mirroring;
    s.irqEnabled = this.irqEnabled;
    s.irqCounter = this.irqCounter;
    s.irqLatch = this.irqLatch;
    s.irqAsserted = this.irqAsserted;
    s.irqLatchMode = this.irqLatchMode;
    s.eepromData = Array.from(this.eeprom.data);
    s.eepromBus = this.eeprom.toJSON();
    return s;
  };

  Mappers[16].prototype.fromJSON = function (s) {
    Mappers[0].prototype.fromJSON.apply(this, arguments);

    this.chrBanks = [0, 0, 0, 0, 0, 0, 0, 0];
    if (s.chrBanks) {
      for (var i = 0; i < 8; i++) {
        this.chrBanks[i] = s.chrBanks[i] || 0;
      }
    }
    this.prgBank = s.prgBank || 0;
    this.mirroring = s.mirroring || 0;
    this.irqEnabled = s.irqEnabled === true;
    this.irqCounter = s.irqCounter || 0;
    this.irqLatch = s.irqLatch || 0;
    this.irqAsserted = s.irqAsserted === true;
    this.irqLatchMode = s.irqLatchMode !== false;

    if (s.eepromData) {
      this.eeprom.data.set(s.eepromData);
    }
    this.eeprom.fromJSON(s.eepromBus);

    this.applyBanks();
    this.applyMirroring();
  };
}

module.exports = { register: register };
